import { yupResolver } from '@hookform/resolvers/yup';
import { Stack, useNavigation } from 'expo-router';
import { useEffect, useState } from 'react';
import { FormProvider, useController, useForm } from 'react-hook-form';
import { StyleSheet } from 'react-native-unistyles';
import { useMutation } from 'urql';
import * as Yup from 'yup';
import FormSaveButton from '@/components/Custom/FormSaveButton';
import ModalKeyboardAvoidingView from '@/components/Custom/ModalKeyboardAvoidingView';
import Box from '@/components/UI/Box';
import Text from '@/components/UI/Text';
import TextInput from '@/components/Unistyles/TextInput';
import { graphql } from '@/gql/graphql';
import useHeaderBackButton from '@/hooks/useHeaderBackButton';
import useTimer from '@/hooks/useTimer';
import useRootStore from '@/stores/useRootStore';
import getAuth, { signInWithEmailLink } from '@/utils/auth';

const SignInEmailSend = graphql(/* GraphQL */ `
  mutation EmailSignInLink($email: String!) {
    emailSignInLink(email: $email) {
      email
    }
  }
`);

export default function SignInEmail() {
  const [, emailSignInLink] = useMutation(SignInEmailSend);
  const navigation = useNavigation();
  const pendingEmailLink = useRootStore((state) => state.pendingEmailLink);
  const [isDisabled, setIsDisabled] = useTimer(false, 5000);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const headerLeft = useHeaderBackButton(() => {
    useRootStore.setState({ pendingEmailLink: null });
    navigation.goBack();
  });

  useEffect(() => {
    if (!pendingEmailLink) return;

    return navigation.addListener('beforeRemove', () => {
      useRootStore.setState({ pendingEmailLink: null });
    });
  }, [navigation, pendingEmailLink]);

  const form = useForm({
    defaultValues: {
      email: '',
    },
    resolver: yupResolver(
      Yup.object().shape({
        email: Yup.string()
          .email('Invalid email address')
          .required('Email address is required'),
      }),
    ),
  });

  const textController = useController({
    control: form.control,
    name: 'email',
  });

  const onPressSend = async ({ email }: { email: string }) => {
    if (pendingEmailLink) {
      try {
        await signInWithEmailLink(getAuth(), email, pendingEmailLink);
        useRootStore.setState({
          magicLinkEmail: null,
          pendingEmailLink: null,
        });
        setErrorMessage(null);
      } catch {
        setErrorMessage(
          'Could not complete sign in with that email. Please try again.',
        );
      }
      return;
    }

    const { error } = await emailSignInLink({ email });
    setIsDisabled(true);

    if (error) {
      setErrorMessage('Could not send sign in link. Please try again later.');

      return;
    }
    setErrorMessage(null);

    useRootStore.setState({ magicLinkEmail: email });
  };

  return (
    <FormProvider {...form}>
      <ModalKeyboardAvoidingView>
        <Box style={style.container}>
          <Stack.Screen
            options={{
              title: 'Sign in with Email',
              headerLeft: pendingEmailLink ? headerLeft : undefined,
            }}
          />
          <Text center>
            {pendingEmailLink
              ? 'Enter the email address that received this sign-in link.'
              : "Enter your Email Address and we'll send you a link that will sign you in."}
          </Text>
          <TextInput
            autoCapitalize="none"
            enterKeyHint="done"
            keyboardType="email-address"
            placeholder="Type Your Email Address..."
            variant="primary"
            autoComplete="email"
            onChangeText={textController.field.onChange}
            value={textController.field.value}
          />
          <FormSaveButton
            footerLayout="centered"
            disabled={
              !form.formState.isValid ||
              form.formState.isSubmitting ||
              isDisabled
            }
            info={{
              text: pendingEmailLink
                ? 'Continue with email link'
                : 'Send sign in link',
            }}
            onPress={form.handleSubmit(onPressSend)}
          />
          {errorMessage ? (
            <Text center variant="error">
              {errorMessage}
            </Text>
          ) : form.formState.isSubmitSuccessful && !pendingEmailLink ? (
            <Text center variant="success">
              Sent sign in link to {form.getValues('email')}.
            </Text>
          ) : null}
        </Box>
      </ModalKeyboardAvoidingView>
    </FormProvider>
  );
}

const style = StyleSheet.create((theme) => ({
  container: {
    flex: 1,
    gap: theme.space('lg'),
    mx: 16,
    alignSelf: 'center',
    justifyContent: 'center',
    maxWidth: theme.size(72),
  },
}));
